import React, { createContext, useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { SupabaseClient } from '@supabase/supabase-js';
import {
  supabase as defaultClient,
  createArtistClient,
  readArtistToken,
  readArtistTokenAsync,
  refreshArtistToken,
  isArtistProtected,
} from '../lib/supabase';
import type { Database } from '../lib/database.types';

/**
 * Provides a Supabase client scoped to the current artist's JWT for
 * RLS-protected artists. Falls back to the global anon-key client for
 * non-protected artists (preserving the original behavior).
 *
 * Used on /artist/:id/* routes. Wrap the route element with this provider
 * (inside ArtistAuthGuard, which ensures the token is minted first).
 */
type ArtistClientContextValue = {
  client: SupabaseClient<Database>;
  isProtected: boolean;
  loading: boolean;
};

const ArtistClientContext = createContext<ArtistClientContextValue>({
  client: defaultClient,
  isProtected: false,
  loading: false,
});

export const useArtistClient = () => useContext(ArtistClientContext);

export const ArtistClientProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { id } = useParams<{ id: string }>();
  const [client, setClient] = useState<SupabaseClient<Database>>(defaultClient);
  const [isProtected, setIsProtected] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    let refreshTimer: ReturnType<typeof setTimeout>;

    async function resolve() {
      if (!id) {
        if (mounted) setLoading(false);
        return;
      }

      console.log('[ArtistClient] Resolving client for artist:', id);

      try {
        const protectedArtist = await isArtistProtected(id);
        if (!mounted) return;
        console.log('[ArtistClient] isProtected:', protectedArtist, 'for artist:', id);

        setIsProtected(protectedArtist);

        if (protectedArtist) {
          // Use async reader that auto-refreshes if token is expired
          const token = await readArtistTokenAsync();
          if (!mounted) return;
          if (token) {
            console.log('[ArtistClient] Token found — phone:', token.phone, '| expires:', token.expires_at);
            const artistClient = createArtistClient(token.token);
            setClient(artistClient);
            console.log('[ArtistClient] Artist-scoped client created with JWT for phone:', token.phone);

            // Schedule refresh 5 minutes before expiry
            const expiresMs = new Date(token.expires_at).getTime();
            const refreshIn = Math.max(60000, expiresMs - Date.now() - 5 * 60 * 1000);
            console.log('[ArtistClient] Scheduling token refresh in', Math.round(refreshIn / 1000), 'seconds');
            refreshTimer = setTimeout(async () => {
              const refreshed = await refreshArtistToken();
              if (refreshed && mounted) {
                console.log('[ArtistClient] Token refreshed — expires:', refreshed.expires_at);
                const newClient = createArtistClient(refreshed.token);
                setClient(newClient);
                // Schedule next refresh
                const nextExpiresMs = new Date(refreshed.expires_at).getTime();
                const nextRefreshIn = Math.max(60000, nextExpiresMs - Date.now() - 5 * 60 * 1000);
                refreshTimer = setTimeout(async () => {
                  const r2 = await refreshArtistToken();
                  if (r2 && mounted) {
                    setClient(createArtistClient(r2.token));
                  }
                }, nextRefreshIn);
              }
            }, refreshIn);
          } else {
            console.warn('[ArtistClient] No token found — RLS will block queries for protected artist:', id);
            setClient(defaultClient);
          }
        } else {
          console.log('[ArtistClient] Non-protected artist — using default anon client');
          setClient(defaultClient);
        }
      } catch (err) {
        console.error('[ArtistClient] Failed to resolve client:', err);
        setClient(defaultClient);
      } finally {
        if (mounted) {
          setLoading(false);
          console.log('[ArtistClient] Loading complete for artist:', id);
        }
      }
    }

    resolve();
    return () => {
      mounted = false;
      if (refreshTimer) clearTimeout(refreshTimer);
    };
  }, [id]);

  return (
    <ArtistClientContext.Provider value={{ client, isProtected, loading }}>
      {children}
    </ArtistClientContext.Provider>
  );
};
